import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Segments in CLOCKWISE order starting from the TOP (12 o'clock) of the image
// The image has 9 segments, each spanning 40 degrees (360/9)
const SEGMENTS = [{
  id: 1,
  color: '#8B4513',
  title: 'ROBA TRES',
  symbol: '+3',
  desc: 'Roba tres cartas del centro'
}, {
  id: 2,
  color: '#722F37',
  title: 'ROBA DOS',
  symbol: '+2',
  desc: 'Roba dos cartas del centro'
}, {
  id: 3,
  color: '#1B4D3E',
  title: 'COMODÍN',
  symbol: '🃏',
  desc: 'Roba un comodín y acomódalo en el juego'
}, {
  id: 4,
  color: '#6B8E23',
  title: 'DESCARTA UNA',
  symbol: '-1',
  desc: 'Escoge una carta de tu mano y ponla en la pila hasta abajo'
}, {
  id: 5,
  color: '#9ACD32',
  title: 'DOBLE TURNO',
  symbol: 'x2',
  desc: 'Roba una carta, juega y vuelve a girar'
}, {
  id: 6,
  color: '#D2691E',
  title: 'ROBA UNA',
  symbol: '+1',
  desc: 'Roba una carta del centro'
}, {
  id: 7,
  color: '#2F2F2F',
  title: 'REVERSA SIN JUGAR',
  symbol: '↺',
  desc: 'Se invierte el sentido del juego'
}, {
  id: 8,
  color: '#1A1A1A',
  title: 'ROBA UNA A JUGADOR',
  symbol: '👤',
  desc: 'Roba una carta de cualquiera de los jugadores'
}, {
  id: 9,
  color: '#111111',
  title: 'COMODÍN',
  symbol: '🃏',
  desc: 'Roba un comodín y acomódalo en el juego'
}];
const SEGMENT_COUNT = SEGMENTS.length; // 9
const SEGMENT_ANGLE = 360 / SEGMENT_COUNT; // 40 degrees each
// Angular offset to calibrate the image segments with the calculation.
const OFFSET_ANGLE = 20;
export function RummyWheel() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [selectedSegment, setSelectedSegment] = useState<(typeof SEGMENTS)[0] | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);
  const calculateWinner = (finalRotation: number) => {
    const normalizedAngle = ((360 - finalRotation % 360 + OFFSET_ANGLE) % 360 + 360) % 360;
    const rawIndex = Math.floor(normalizedAngle / SEGMENT_ANGLE) % SEGMENT_COUNT;
    const index = rawIndex;
    return SEGMENTS[index];
  };
  const spinWheel = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setSelectedSegment(null);
    // Random rotation: 5-10 full spins + random offset
    const minSpins = 5;
    const maxSpins = 10;
    const randomSpins = Math.random() * (maxSpins - minSpins) + minSpins;
    const randomDegree = Math.floor(randomSpins * 360) + Math.floor(Math.random() * 360);
    const newRotation = rotation + randomDegree;
    setRotation(newRotation);
    // Calculate winner based on final position
    const winner = calculateWinner(newRotation);
    // Wait for animation to finish (4s matches CSS transition)
    setTimeout(() => {
      setIsSpinning(false);
      setSelectedSegment(winner);
    }, 4200);
  };
  return <div className="flex flex-col items-center justify-center min-h-screen bg-[#121212] p-4 font-sans text-white overflow-hidden relative" data-id="element-2">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{
      backgroundColor: '#000000'
    }} data-id="element-3">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-900/20 rounded-full blur-[100px]" data-id="element-4" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-900/20 rounded-full blur-[100px]" data-id="element-5" />
      </div>

      {/* Header */}
      <div className="z-10 text-center mb-20" data-id="element-6">
        <h1 className="text-4xl md:text-6xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 drop-shadow-lg transform -rotate-2" data-id="element-7">
          RUMMY SPEED
        </h1>
        <p className="text-gray-400 mt-2 font-medium tracking-wide uppercase text-sm" data-id="element-8">
          ¡Gira la ruleta y juega tu turno!
        </p>
      </div>

      {/* Main Wheel Container */}
      <div className="relative z-10 mb-8 group" data-id="element-9">
        {/* Pointer (Static) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 z-30 filter drop-shadow-lg" data-id="element-10">
          <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[40px] border-t-blue-500 relative" data-id="element-11">
            <div className="absolute -top-[42px] -left-[20px] w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[40px] border-t-blue-400 scale-90" data-id="element-12"></div>
          </div>
        </div>

        {/* Spinning Wheel - Full image, no clipping */}
        <div ref={wheelRef} className="overflow-hidden shadow-2xl" style={{
        width: 'min(90vw, 420px)',
        height: 'min(90vw, 420px)',
        transform: `rotate(${rotation}deg)`,
        transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
      }} data-id="element-13">
          <img src="https://cdn.magicpatterns.com/uploads/p7gLm6VGrExqmd2rQWZksN/rummy_fondo_negro_v2.png" alt="Rummy Speed Wheel" className="w-full h-full object-contain" draggable={false} data-id="element-14" />
        </div>
      </div>

      {/* Controls & Result */}
      <div className="z-10 w-full max-w-md flex flex-col items-center min-h-[180px]" data-id="element-15">
        <AnimatePresence mode="wait" data-id="element-16">
          {!selectedSegment && !isSpinning ? <motion.button initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={spinWheel} className="bg-red-600 hover:bg-red-500 text-white text-2xl font-black py-4 px-12 rounded-full shadow-[0_4px_0_rgb(153,27,27)] active:shadow-none active:translate-y-[4px] transition-all uppercase tracking-widest animate-pulse" data-id="element-17">
              GIRAR
            </motion.button> : isSpinning ? <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="text-xl font-bold text-white/80 animate-pulse" data-id="element-18">
              Girando...
            </motion.div> : <motion.div key="result" initial={{
          opacity: 0,
          scale: 0.8,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.8,
          y: -20
        }} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 w-full text-center shadow-2xl relative overflow-hidden" data-id="element-19">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent" data-id="element-20" />

              <div className="flex flex-col items-center gap-2" data-id="element-21">
                <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={spinWheel} className="mb-2 bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-6 rounded-full text-sm shadow-[0_2px_0_rgb(153,27,27)] active:shadow-none active:translate-y-[2px] transition-all" data-id="element-22">
                  Girar de nuevo
                </motion.button>

                <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl shadow-inner mb-2 border-2 border-white/20" style={{
              backgroundColor: selectedSegment?.color
            }} data-id="element-23">
                  <span className="filter drop-shadow-md" data-id="element-24">
                    {selectedSegment?.symbol}
                  </span>
                </div>

                <h2 className="text-2xl font-black text-white uppercase tracking-wide" data-id="element-25">
                  {selectedSegment?.title}
                </h2>

                <p className="text-gray-300 font-medium text-lg leading-relaxed" data-id="element-26">
                  {selectedSegment?.desc}
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}